<template>
  <div class="page-layout" data-track="Competitor Report View">
    <div class="columns">
      <div class="is-12-mobile is-12-desktop is-12-widescreen file-mobile p-x-0">
        <div class="level">
          <div class="level-left">
            <div class="page-layout-head">Competitors</div>
          </div>
          <div class="level-right">
            <date-select-filter :filter_items="dateDropdown"
                                class="date-filter padding-top-0 padding-right-0"
                                v-on:onDateChange="this.filterCompetitorData"
                                data-track="Change Competitor Date range"/>
            <a v-if="hasEligibleSubsc" :href="exportSummary" class="button hollow-btn is-hidden-mobile competitor-export" data-track="Competitor Summary export">EXPORT</a>
          </div>
        </div>
      </div>
    </div>

    <hr class="page-layout-horizontal-row">

    <template v-if="hasEligibleSubsc">
      <div>
        <span class="page-layout-head margin-right-5">Compare</span>
        <span class="light-text">(Max of 5 businesses)</span>
      </div>
      <p class="has-text-centered" v-if="isSummaryReady > 0">
        <img alt="Fetching chart data..." class="spinner" src="/static/img/loaders/spinner.svg"/>
      </p>
      <table v-else class="table is-fullwidth summary-table">
        <thead>
        <tr>
          <td>Key</td>
          <td>Business</td>
          <td>
            <sort-by-col group="competitorSummary" col="trustindex">TrustIndex</sort-by-col>
          </td>
          <td>
            <sort-by-col group="competitorSummary" col="rank">Industry ranking</sort-by-col>
          </td>
          <td>
            <sort-by-col group="competitorSummary" col="nps">NPS</sort-by-col>
          </td>
          <td>
            <sort-by-col group="competitorSummary" col="reviewsCount">Total&nbsp;reviews</sort-by-col>
          </td>
          <td>
            <sort-by-col group="competitorSummary" col="rating">Average star rating</sort-by-col>
          </td>
          <td>
            <sort-by-col group="competitorSummary" col="replyTime">Average time to reply</sort-by-col>
          </td>
          <td>&nbsp;</td>
        </tr>
        </thead>
        <tfoot v-if="!maxBusinessSelected">
        <tr>
          <td>
            <colored-checkbox :is-disabled="true" color="#fff"></colored-checkbox>
          </td>
          <td>
            <suggestion-input :field="{placeholder:'Type the business name'}" :results="searchResults" @suggestion-input="businessLookup($event)"
                              @suggestion-selected="addBusiness($event)"/>
          </td>
          <td class="pad-left-25" colspan="7">
            &nbsp;
          </td>
        </tr>
        </tfoot>
        <tbody>
        <tr v-for="record in summaryTotals">
          <td>
            <colored-checkbox :color="record.color" :is-checked="true"
                              @cb-toggle="toggleSelectedBiz($event, record.business_id)"></colored-checkbox>
          </td>
          <td v-text="record.name"></td>
          <td class="pad-left-25">
            <template v-if="'trust_index' in record">
              {{ record.trust_index }}

              <template v-if="record.trust_index_sensex[0] == '-'">
                <img src="/static/img/competitors/sensex_down.svg" class="sensex-image">
                <span class="sensex-text sensex-text-down">{{ Math.abs(record.trust_index_sensex) }}%</span>
              </template>
              <template v-else-if="record.trust_index_sensex != '0.0'">
                <img src="/static/img/competitors/sensex_up.svg" class="sensex-image">
                <span class="sensex-text sensex-text-up">{{ record.trust_index_sensex }}%</span>
              </template>
            </template>
            <template v-else>NA</template>
          </td>
          <td class="pad-left-25">
            <template v-if="record.industry_rank">{{ record.industry_rank }}</template>
            <template v-else>NA</template>
          </td>
          <td class="pad-left-25">
            <template v-if="'nps_total' in record">
              {{ Math.floor(record.nps_total) }}
            </template>
            <template v-else>NA</template>
          </td>
          <td class="pad-left-25">
            <template v-if="'total_reviews' in record">
              {{ record.total_reviews }}
            </template>
            <template v-else>NA</template>
          </td>
          <td v-if="'average_rating' in record">
            <div class="is-flex">
              <rating-star :stars="record.average_rating"
                           :is-avg="true"
                           class="pad-left-12 margin-right-20 has-width-auto star-strip"></rating-star>
              {{ record.average_rating }}
            </div>
          </td>
          <td class="pad-left-25" v-else>NA</td>
          <td class="pad-left-25">{{ record.reply_time }}</td>
          <td>
            <a @click.prevent="removeBusiness(record.business_id)"
               data-track="Remove business from competitor list"
               href="#"
               v-if="record.business_id !== myBusinessId">
              <img src="/static/img/competitors/clear.svg"/>
            </a>
          </td>
        </tr>
        </tbody>
      </table>

      <custom-tabs
        :tabs="[{'name':'TrustIndex', 'components':['TrustIndexCompare'], 'url':'/analytics/competitors/trust-index', 'props':{dateRange:filterDates, biz:enabledBiz}, 'track':'Show Competitor TrustIndex'}, {'name':'NPS', 'components':['NPSCompare'], 'url':'/analytics/competitors/nps', 'props':{dateRange:filterDates, biz:enabledBiz, summary:summaryTotals}, 'track':'Show Competitor NPS Score'}, {'name':'Total reviews', 'components':['ReviewsCompare'], 'url':'/analytics/competitors/reviews', 'props':{dateRange:filterDates, biz:enabledBiz}, 'track':'Show Competitor Total reviews'}, {'name':'Average star rating', 'components':['RatingsCompare'], 'url':'/analytics/competitors/ratings', 'props':{dateRange:filterDates, biz:enabledBiz, summary:summaryTotals}, 'track':'Show Competitor Average star rating'}, {'name':'Time to reply', 'components':['ReplyCompare'], 'url':'/analytics/competitors/reply', 'props':{dateRange:filterDates, biz:enabledBiz, summary:summaryTotals}, 'track':'Show Competitor Time to reply'}]"
        class="margin-bottom-50"/>
    </template>
    <template v-else>
      <div class="upgrade-box">
        <div class="upgrade-box__header">
          Upgrade
          <img src="/static/img/competitors/lock.svg" align="right"/>
        </div>
        <div class="upgrade-box__main">
          Here, you’ll find an in-depth breakdown comparison of you and your competitors TrustIndex score, total reviews, the average time to reply, NPS, and star rating
          within a graph. <a href="/billing/edit-plan/p1/annual" class="blue-link">Upgrade</a> your Plan to gain access to your competitor analytics.
        </div>
      </div>
      <img src="/static/img/competitors/showcase.png" class="margin-top-25"/>
    </template>
  </div>
</template>

<script>
/* eslint-disable indent,no-new,no-undef */
import qs from 'qs'
import RatingStar from '../../../components/Rating.vue'
import moment from 'moment'
import axios from 'axios'
import {ColoredCheckbox, CustomTabs, DateSelectFilter, SortByCol, SuggestionInput} from '@/components'
import {misc, SearchFilterMenus} from '@/mixins'

export default {
  name: 'CompetitorReport',
  metaInfo: {
    title: 'Business Portal | Hellopeter - Competitors Index'
  },
  mounted () {
    this.$gtm.trackEvent({
      event: 'pageLoad',
      virtualPageUrl: this.$router.currentRoute.path,
      virtualPageTitle: 'Competitors Index'
    })
  },
  components: {
    ColoredCheckbox,
    RatingStar,
    SortByCol,
    DateSelectFilter,
    CustomTabs,
    SuggestionInput
  },
  mixins: [SearchFilterMenus, misc],
  data () {
    return {
      dateDropdown: [{
        name: 'date',
        label: 'Date',
        type: 'radio',
        default: {type: 'months', value: 6, selected: 'Last 6 months'}, // moment.js placeholder in type
        elements: this.buildDateDropdownItems()
      }],
      filterDates: {
        'start_date': '',
        'end_date': ''
      },
      billingDetails: JSON.parse(localStorage.getItem('billingDetails')),
      myBusinessId: parseInt(localStorage.getItem('org_id')),
      myBusinessName: localStorage.getItem('org_name'),
      myBusinessSlug: localStorage.getItem('org_slug'),
      myIndustryId: parseInt(localStorage.getItem('org_industry_id')),
      selectedBusinesses: [],
      businesses: {},
      searchResults: {},
      businessColours: [
        '#17BEBB',
        '#FFBF00',
        '#F46036',
        '#A959D3',
        '#2F5597'
      ],
      summaryTotals: [],
      exportPayload: {},
      isSummaryReady: 0
    }
  },
  created () {
    this.getBizCompetitorsReportHistory()
      .then(() => {
        window.events.$on('sort-changed', data => {
          if (data.group === 'competitorSummary') {
            this.summaryTotals.sort((a, b) => {
              let firstVal = ''
              let secondVal = ''
              if (data.col === 'trustindex') {
                firstVal = parseFloat(a.trust_index)
                secondVal = parseFloat(b.trust_index)
              } else if (data.col === 'rank') {
                firstVal = a.industry_rank
                secondVal = b.industry_rank
              } else if (data.col === 'nps') {
                firstVal = Math.floor(a.nps_total)
                secondVal = Math.floor(b.nps_total)
              } else if (data.col === 'reviewsCount') {
                firstVal = a.total_reviews
                secondVal = b.total_reviews
              } else if (data.col === 'rating') {
                firstVal = parseFloat(a.average_rating)
                secondVal = parseFloat(b.average_rating)
              } else if (data.col === 'replyTime') {
                firstVal = parseFloat(a.reply_time)
                secondVal = parseFloat(b.reply_time)
              }

              if (data.order === 'asc') {
                return firstVal - secondVal
              } else {
                return secondVal - firstVal
              }
            })
          }
        })

        window.events.$on('flash-error', (data) => {
          if (data.group === 'competitor-report') {
            this.flashErrMsg(data.msg, data.gtmName)
          }
        })
      })
  },
  destroyed () {
    window.events.$off('sort-changed')
    window.events.$off('flash-error')
  },
  computed: {
    exportSummary () {
      const businesses = this.selectedBusinesses.map(({id}) => id)
      const payload = {
        businesses: businesses,
        startDate: this.filterDates.start_date,
        endDate: this.filterDates.end_date,
        track_activity_action: 'competitors.summary.view',
        track_activity_event: 'CompetitorAnalyticsExportActivity'
      }
      this.exportPayload = payload
      const url = qs.stringify(payload)
      return process.env.API_URL + `/competitors/export?${url}`
    },
    hasEligibleSubsc () {
      const allowedPlans = ['Pro', 'Enterprise', 'P1', 'P2', 'P3', 'P4']
      return allowedPlans.indexOf(this.billingDetails.subscription.name) !== -1
    },
    enabledBiz () {
      return this.selectedBusinesses.filter(biz => {
        return biz.enabled
      })
    },
    maxBusinessSelected () {
      return this.selectedBusinesses.length === 5
    }
  },
  methods: {
    flashErrMsg (msg, gtmName) {
      this.displayErrMsg({response: {data: {error: msg}}}, gtmName)
    },
    toggleSelectedBiz (isChecked, bizId) {
      const idx = this.selectedBusinesses.findIndex(biz => biz.id === bizId)
      this.selectedBusinesses[idx].enabled = isChecked
    },
    filterCompetitorData (dates) {
      this.filterDates = {
        'start_date': dates.startDate,
        'end_date': dates.endDate
      }
      this.fetchSummaryData()
    },
    /**
     * Get business competitors report histroy
     * @returns {void}
     */
    async getBizCompetitorsReportHistory () {
      ++this.isSummaryReady
      return axios.get(`competitors/${this.myBusinessSlug}/get-competitors`)
        .then(({data}) => {
          this.addBusinessUi(this.myBusinessId, this.myBusinessName, this.myBusinessSlug)
          for (let i in data.competitors_data) {
            this.addBusinessUi(data.competitors_data[i].id, data.competitors_data[i].name, data.competitors_data[i].slug)
          }
          this.fetchSummaryData()
          this.isSummaryReady--
        })
    },
    /**
     * Add Business to competitiors
     *
     * @param {string} slug The slug of the Business used for display
     *
     * @returns {void}
     */
    addBusiness (slug) {
      ++this.isSummaryReady
      const id = this.businesses[slug].id
      if (this.myBusinessId !== id) {
        axios.post(`/competitors/${this.myBusinessSlug}/add-competitors`, {'competitorBiz': slug})
          .then(() => {
            this.addBusinessUi(id, this.businesses[slug].name, slug)
            this.fetchSummaryData()
            this.isSummaryReady--
          })
      }
    },
    /**
     * Add Business to competitors on UI
     *
     * @param {number} id The Business ID
     * @param {string} name The name of the Business used for display
     * @param {string} slug The slug of the Business used for display
     *
     * @returns {void}
     */
    addBusinessUi (id, name, slug) {
      let nextAvailableColor
      this.businessColours.forEach((color) => {
        const usedColor = this.selectedBusinesses.find(business => business.color === color)
        if (typeof usedColor === 'undefined' && typeof nextAvailableColor === 'undefined') {
          nextAvailableColor = color
        }
      })
      this.selectedBusinesses.push({
        id: id,
        name: name,
        color: nextAvailableColor,
        enabled: true
      })
      this.selectedBusinesses.sort((a, b) => a.id - b.id)
    },
    /**
     * Remove a Business from the list of the Selected Businesses and re-sync
     *
     * @param {number} id The Business ID
     *
     * @returns {void}
     */
    removeBusiness (id) {
      axios.post(`/competitors/${this.myBusinessSlug}/delete-competitors`, {'competitorBizId': id})
        .then((data) => {
          let removeBusinessReference = this.selectedBusinesses.map(business => business.id).indexOf(id)
          this.selectedBusinesses.splice(removeBusinessReference, 1)
          let removeSummaryReference = this.summaryTotals.map(business => business.business_id).indexOf(id)
          this.summaryTotals.splice(removeSummaryReference, 1)
          this.selectedBusinesses.sort((a, b) => a.id - b.id)
        })
    },
    /**
     * Dynamically build the list of options so that we're always giving the user the option
     * to select month-to-month based on the most recent month in comparison to the current one
     *
     * @return {Array}
     */
    buildDateDropdownItems () {
      let options = ['Last 6 months', 'Last 12 months', 'Previous calendar year']

      let currentDate = moment()
      let stopDate = moment()
      currentDate.startOf('month').startOf('day')
      stopDate.startOf('month').startOf('day')
      stopDate.subtract(14, 'months')
      currentDate.subtract(1, 'month')
      while (currentDate.isAfter(stopDate)) {
        const monthYear = currentDate.format('MMMM YYYY')
        options.push(monthYear)
        currentDate.subtract(1, 'month')
      }

      return options
    },

    /**
     * Autocomplete lookup Businesses that have not already been selected
     *
     * @param {String} string The Business string to look for
     * @returns {void}
     */
    businessLookup (string) {
      if (string.length < 2) {
        this.searchResults = {}
      } else {
        let keyword = encodeURIComponent(string)
        let ignoreList = this.selectedBusinesses.map(business => business.id).join(',')
        axios.get(`/search/business?name=${keyword}&ignore=${ignoreList}`)
          .then(({data}) => {
            const results = {}
            if (data.length) {
              const businesses = {}
              data.forEach(value => {
                results[value.slug] = {'label': value.name, 'props': {'data-track': 'Add business to competitor list'}}
                businesses[value.slug] = value
              })
              this.businesses = businesses
            }
            this.searchResults = results
          })
      }
    },
    /**
     * Fetch the Summary data used to display the Summary Table
     * This call automatically also retrieves the data required for the NPS Totals listing below the
     * NPS Breakdown graph
     *
     * @returns {void}
     */
    fetchSummaryData () {
      if (!this.selectedBusinesses.length) {
        console.log('No businesses selected')
        return
      }

      ++this.isSummaryReady
      const jsonBody = {
        dates: this.filterDates,
        businesses: this.selectedBusinesses
      }
      axios.post(`/competitors/summary`, jsonBody)
        .then(({data}) => {
          this.selectedBusinesses.forEach(biz => {
            const idx = data.findIndex(apiBiz => apiBiz.business_id === biz.id)
            if (idx > -1) {
              data[idx].color = biz.color
              data[idx].name = biz.name
            } else {
              data.push({business_id: biz.id, color: biz.color, name: biz.name})
            }
            if (data[idx]) {
              data[idx].average_rating = parseFloat(data[idx].average_rating)
            }
          })
          this.summaryTotals = data
          this.isSummaryReady--
        })
        .catch(() => {
          this.flashErrMsg('There was an error retrieving the summary data, please try again later', 'Failed loading summary data on Competitors Index View')
        })
    }
  }
}
</script>

<style scoped>
.spinner {
  width: 75px;
}

.light-text {
  font-size: 11px;
  color: #58595b;
  opacity: 0.5;
}

.sensex-image {
  vertical-align: middle;
}

.sensex-text {
  font-size: 10px;
  font-weight: bold;
}

.sensex-text-up {
  color: #00bf87;
}

.sensex-text-down {
  color: #ea3939;
}

.pad-left-25 {
  padding-left: 25px !important;
}

.star-strip {
  margin-top: 3px !important;
  height: 11px;
}

.date-filter {
  width: 275px;
}

.date-filter >>> .button {
  padding: 8px 0;
  min-height: auto !important
}

.date-filter >>> .button.button-dropdown img {
  height: 17px;
  padding: 0;
}

.dropdown-menu {
  border-top: 3px solid #30AAE1;
  width: 100%;
  padding-top: 0 !important;
  border-bottom: 1px solid #e1dfdf;
}

a.dropdown-item:hover {
  background-color: #30AAE1;
  color: #fff;
}

.dropdown-content {
  border-radius: 0;
  overflow-y: scroll;
  height: 200px;
}

.dropdown-item {
  padding: 10px !important;
}

.competitor-export {
  width: 130px;
  margin-left: 15px;
}

.summary-table .has-width-auto >>> img {
  height: 8px;
}
</style>



// WEBPACK FOOTER //
// src/pages/analytics/competitors/CompetitorIndex.vue